import { Badge, Code, Flex, Heading } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SessionVisualizer } from 'components/common/sessions/dialogs/visualize-session/visualizer';
import { CommonTableHoC } from 'components/common/table';
import { CommonTableButton } from 'components/common/table/button';
import { lightFormat, startOfToday, subMonths } from 'date-fns';
import { ACTIONS_KEY } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { IFullDialog } from 'interfaces/i-dialogs';
import { RowWithID } from 'interfaces/tables/main';
import { ITablePageable } from 'interfaces/tables/pagination';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { IUser } from 'lib_ts/interfaces/i-user';
import React from 'react';
import { SessionEventsService } from 'services/session-events.service';

const COMPONENT_NAME = 'ViewSessionsDialog';

const START_DATE = subMonths(startOfToday(), 1);

interface IProps {
  user: IUser;
  onClose: () => void;
}

interface ISessionSummaryRow extends ISessionSummary, RowWithID {}

interface IState {
  selected?: ISessionSummaryRow;
  summaries?: ISessionSummaryRow[];
}

export class ViewSessionsDialog extends React.Component<IProps, IState> {
  private init = false;

  constructor(props: IProps) {
    super(props);

    /** make a copy to manipulate via forms */
    this.state = {};
  }

  componentDidMount(): void {
    if (this.init) {
      return;
    }

    this.init = true;

    SessionEventsService.summarizeUserSessions({
      machineIDs: [this.props.user.machineID],
      userID: this.props.user._id,
      startDate: START_DATE.toISOString(),
    })
      .then((results) => {
        const sessionsWithIDs = StringHelper.addIds(results, 'sessionID');
        this.setState({
          summaries: sessionsWithIDs.sort(
            (a, b) => -StringHelper.safeSort(a.startDate, b.startDate)
          ),
        });
      })
      .catch((error) => console.error(error));
  }

  render() {
    const data = this.state.summaries ?? [];

    const pagination: ITablePageable = {
      enablePagination: true,
      pageSizes: [10],
      paginationID: PaginationID.ViewSessions,
      total: data.length,
    };

    const mergeProps: IFullDialog = {
      identifier: COMPONENT_NAME,
      title: 'admin.view-sessions',
      width: RADIX.DIALOG.WIDTH.LG,
      content: (
        <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
          <Heading size={RADIX.HEADING.SIZE.SM}>
            Recent Sessions for{' '}
            <Badge color={RADIX.COLOR.WARNING}>{this.props.user.email}</Badge> -
            only sessions newer than{' '}
            {lightFormat(START_DATE, LOCAL_DATE_FORMAT)} will be loaded
          </Heading>

          <CommonTableHoC
            dataTestID="ViewSessions"
            identifier="ViewSessionsTable"
            loading={!this.state.summaries}
            displayColumns={[
              {
                key: 'session',
                label: 'Session ID',
                formatFn: (s: ISessionSummaryRow) => <Code>{s.sessionID}</Code>,
              },
              {
                key: 'startDate',
                label: 'common.created',
                dataType: 'datetime',
              },
              {
                key: 'totalFires',
                label: 'common.shots',
                align: 'right',
              },
              {
                key: ACTIONS_KEY,
                label: 'common.actions',
                formatFn: (s: ISessionSummaryRow) => {
                  const isActive =
                    this.state.selected?.sessionID === s.sessionID;

                  return (
                    <CommonTableButton
                      disabled={isActive}
                      color={isActive ? RADIX.COLOR.SUCCESS : RADIX.COLOR.INFO}
                      onClick={() => this.setState({ selected: s })}
                      label={isActive ? 'common.active' : 'common.select'}
                    />
                  );
                },
              },
            ]}
            displayData={data}
            {...pagination}
          />

          {this.state.selected && (
            <>
              <Heading size={RADIX.HEADING.SIZE.MD}>
                Active: <Code>{this.state.selected.sessionID}</Code>
              </Heading>
              <SessionVisualizer session={this.state.selected} />
            </>
          )}
        </Flex>
      ),
      onClose: () => this.props.onClose(),
    };

    return (
      <ErrorBoundary identifier={COMPONENT_NAME}>
        <CommonDialog {...mergeProps} />
      </ErrorBoundary>
    );
  }
}
